import { z } from 'zod';
import { paymentMethodSchema } from './clinic.js';
/**
 * Contas a pagar, contas e caixas da clínica (estudo do financeiro, etapa 3). O APOLO não
 * tem nada disso: a aba Caixa dele não grava lançamento. Aqui a conta a pagar tem
 * fornecedor, categoria de clínica, vencimento, recorrência ou parcelas, e sai de uma conta
 * (banco, caixa da recepção ou a operadora), que tem o seu saldo.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const isoDateTime = z.iso.datetime();
const cents = z.number().int().min(1).max(100_000_000);
export const CLINIC_ACCOUNT_KINDS = ['banco', 'caixa', 'operadora'];
export const clinicAccountKindSchema = z.enum(CLINIC_ACCOUNT_KINDS);
export const CLINIC_ACCOUNT_KIND_LABEL = {
    banco: 'Banco',
    caixa: 'Caixa da recepção',
    operadora: 'Operadora do cartão',
};
export const clinicAccountSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    kind: clinicAccountKindSchema,
    /** Saldo no dia em que a conta começou a ser acompanhada aqui. */
    openingCents: z.number().int(),
    openingDay: isoDate,
    active: z.boolean(),
    /** Saldo de hoje: abertura + o que entrou − o que saiu por ela. */
    balanceCents: z.number().int(),
});
export const saveClinicAccountSchema = z.object({
    name: z.string().trim().min(2).max(60),
    kind: clinicAccountKindSchema,
    openingCents: z.number().int().min(-100_000_000).max(100_000_000).optional(),
    openingDay: isoDate.optional(),
    active: z.boolean().optional(),
});
/** Categorias de quem tem clínica (dá para escrever outra no campo). */
export const CLINIC_BILL_CATEGORIES = [
    'Aluguel e condomínio',
    'Pessoas (salários, pró-labore, encargos)',
    'Medicamentos e materiais',
    'Taxas de cartão e banco',
    'Impostos',
    'Marketing',
    'Sistemas e assinaturas',
    'Contador',
    'Manutenção e limpeza',
    'Outros',
];
/** Categorias que contam como despesa fixa (ponto de equilíbrio, etapa 5). */
export const CLINIC_FIXED_CATEGORIES = [
    'Aluguel e condomínio',
    'Pessoas (salários, pró-labore, encargos)',
    'Sistemas e assinaturas',
    'Contador',
    'Manutenção e limpeza',
];
export const CLINIC_BILL_RECURRENCES = ['nenhuma', 'mensal', 'anual'];
export const clinicBillSchema = z.object({
    id: z.uuid(),
    supplier: z.string(),
    category: z.string(),
    description: z.string(),
    amountCents: z.number().int(),
    dueDay: isoDate,
    paidAt: isoDateTime.nullable(),
    accountId: z.uuid().nullable(),
    accountName: z.string().nullable(),
    method: paymentMethodSchema.nullable(),
    recurrence: z.enum(CLINIC_BILL_RECURRENCES),
    /** Mesmo id para as parcelas e as repetições de uma conta. */
    seriesId: z.uuid().nullable(),
    part: z.number().int().min(1).nullable(),
    parts: z.number().int().min(1).nullable(),
    /** Linha digitável do boleto, para copiar na hora de pagar. */
    barcode: z.string().nullable(),
    /** Nasceu do pedido de medicamento feito ao fornecedor. */
    medicationOrderId: z.uuid().nullable(),
    /** Derivado: paga, a vencer, vencida (sem pagar até ontem) ou cancelada. */
    status: z.enum(['paga', 'a_vencer', 'vencida', 'cancelada']),
});
export const createClinicBillSchema = z.object({
    supplier: z.string().trim().min(2).max(80),
    category: z.string().trim().min(2).max(60),
    description: z.string().trim().max(120).optional(),
    amountCents: cents,
    dueDay: isoDate,
    method: paymentMethodSchema.optional(),
    /** Mensal: as próximas 12; anual: as próximas 2. */
    recurrence: z.enum(CLINIC_BILL_RECURRENCES).optional(),
    /** Parcelada: o valor é dividido em tantas vezes, uma por mês. */
    parts: z.number().int().min(1).max(24).optional(),
    barcode: z.string().trim().max(120).optional(),
});
export const updateClinicBillSchema = z
    .object({
    supplier: z.string().trim().min(2).max(80),
    category: z.string().trim().min(2).max(60),
    description: z.string().trim().max(120),
    amountCents: cents,
    dueDay: isoDate,
    method: paymentMethodSchema.nullable(),
    barcode: z.string().trim().max(120).nullable(),
})
    .partial();
export const payClinicBillSchema = z.object({
    paidAt: isoDateTime.optional(),
    accountId: z.uuid().optional(),
    method: paymentMethodSchema.optional(),
});
export const clinicBillsQuerySchema = z.object({ from: isoDate, to: isoDate });
export const clinicBillListSchema = z.object({
    bills: z.array(clinicBillSchema),
    /** O que falta pagar no período e o que já venceu sem pagar (de qualquer mês). */
    openCents: z.number().int(),
    overdueCents: z.number().int(),
    paidCents: z.number().int(),
});
